/**
 * Attachments (RFC 0288): a message carries pointers, never bytes.
 *
 * The bytes of an attachment live in a document of their own, so the session
 * log stays thin: Automerge documents do not shrink, and the log syncs to every
 * peer and device, so a 200KB screenshot inlined onto a message is permanent
 * weight on every future read of that session.
 *
 * This module is the pure half. Everything that needs a DOM (decoding an image
 * to measure it, downscaling it through a canvas) is injected as `processImage`,
 * so the policy here (what is too large, what gets downscaled, what shape the
 * pointer takes) is testable in node with no browser and no repo.
 */

import type { AttachmentRef, DocId } from '../../log/types'

// The pointer shape is defined with the message that carries it (log-types), so
// there is exactly one definition of what goes on the log. Re-exported here so
// callers of attachFile get it from the same module as the function.
export type { AttachmentRef }

/** The document. One per attachment, created before the message is appended. */
export interface AttachmentDoc {
  content: Uint8Array
  mimeType: string
  name: string
  /** Present for images. Lets the thread reserve layout before bytes arrive. */
  width?: number
  height?: number
}

/** A file, reduced to the three things this module needs from it. */
export interface AttachmentSource {
  name: string
  mimeType: string
  bytes: Uint8Array
}

export interface AttachmentLimits {
  /** Longest edge, in pixels, an image is allowed to keep. */
  maxEdge: number
  /** Hard ceiling on stored bytes, applied AFTER any downscale. */
  maxBytes: number
}

/**
 * Borrowed from Claude Code, which ships `sharp` and a `resizeImage` path and
 * fails with "image exceeds the ${maxBase64Size} API limit and compression
 * failed". Its storage choice (inline base64) does not transfer to a CRDT, but
 * its sizing discipline does: a retina screenshot is routinely over 190KB.
 */
export const DEFAULT_ATTACHMENT_LIMITS: AttachmentLimits = {
  maxEdge: 2048,
  maxBytes: 5 * 1024 * 1024,
}

/**
 * Downscale an image to fit `maxEdge`, and report what came back.
 *
 * `mimeType` is REQUIRED in the result, and is the type of the returned bytes,
 * which is not always the type that went in: a canvas cannot necessarily
 * re-encode the format it decoded, so downscaling a WebP may hand back a PNG.
 * Storing the input type against re-encoded bytes produces a document whose
 * declared type is a lie, and the model's API rejects an image block whose
 * media_type does not match its data.
 */
export type ImageProcessor = (
  bytes: Uint8Array,
  mimeType: string,
  maxEdge: number,
) => Promise<{
  bytes: Uint8Array
  mimeType: string
  width: number
  height: number
}>

/** The repo, reduced to the one call this module makes against it. */
export interface AttachmentRepo {
  create(doc: AttachmentDoc): Promise<DocId> | DocId
}

export interface AttachFileDeps {
  repo: AttachmentRepo
  limits?: AttachmentLimits
  /** Omitted outside a browser: images are then stored as-is, unmeasured. */
  processImage?: ImageProcessor
}

export class AttachmentTooLargeError extends Error {
  constructor(
    readonly name_: string,
    readonly bytes: number,
    readonly maxBytes: number,
  ) {
    super(
      `"${name_}" is ${formatBytes(bytes)}, over the ${formatBytes(maxBytes)} limit`,
    )
    this.name = 'AttachmentTooLargeError'
  }
}

export function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/')
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Turn a file into an attachment document plus the pointer that references it.
 *
 * Ordering matters: the document is created BEFORE the message is appended,
 * because `reencodeScalars` treats array and object fields as write-once at
 * append (codec.ts). An `attachments` array cannot be filled in after the fact,
 * so there is no "upload after send" path to fall back on.
 */
export async function attachFile(
  src: AttachmentSource,
  deps: AttachFileDeps,
): Promise<AttachmentRef> {
  const limits = deps.limits ?? DEFAULT_ATTACHMENT_LIMITS
  let bytes = src.bytes
  let mimeType = src.mimeType
  let width: number | undefined
  let height: number | undefined

  if (isImage(src.mimeType) && deps.processImage) {
    const out = await deps.processImage(bytes, src.mimeType, limits.maxEdge)
    bytes = out.bytes
    // The processor's type, not the source's: re-encoding can change the
    // format, and the stored type must describe the stored bytes.
    mimeType = out.mimeType
    // 0 means "could not be decoded to measure". Left absent rather than
    // written as 0, so the renderer reserves no ratio instead of a 0/0 one.
    width = out.width || undefined
    height = out.height || undefined
  }

  // Checked after downscaling: an oversized photo that shrinks under the cap is
  // accepted, which is the common case for a phone screenshot.
  if (bytes.byteLength > limits.maxBytes) {
    throw new AttachmentTooLargeError(
      src.name,
      bytes.byteLength,
      limits.maxBytes,
    )
  }

  const doc: AttachmentDoc = {
    content: bytes,
    mimeType,
    name: src.name,
    ...(width !== undefined ? { width } : {}),
    ...(height !== undefined ? { height } : {}),
  }
  const docId = await deps.repo.create(doc)

  return {
    docId,
    name: src.name,
    mimeType,
    bytes: bytes.byteLength,
    ...(width !== undefined ? { width } : {}),
    ...(height !== undefined ? { height } : {}),
  }
}

/**
 * Base64, in whatever runtime this is.
 *
 * `btoa` in a browser, `Buffer` in node, and a hand-rolled fallback for
 * neither. Chunked because `String.fromCharCode(...bytes)` on a multi-megabyte
 * image blows the argument limit and throws RangeError, which would look like
 * an attachment that mysteriously fails only when it is large.
 */
function toBase64(bytes: Uint8Array): string {
  const g = globalThis as {
    btoa?: (s: string) => string
    Buffer?: { from(b: Uint8Array): { toString(enc: string): string } }
  }
  if (g.Buffer) return g.Buffer.from(bytes).toString('base64')
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  if (g.btoa) return g.btoa(binary)
  throw new Error('no base64 encoder in this runtime')
}

/**
 * The one call this resolver makes against a repo.
 *
 * `id: never` rather than `string` so a real `Repo` is assignable: its `find`
 * takes a BRANDED `AnyDocumentId`, and a plain string parameter here would make
 * every host's repo structurally incompatible. Callers pass `id as never`,
 * which is the pattern already used against this API elsewhere in the repo.
 */
export interface AttachmentReadRepo {
  find(id: never): Promise<{
    whenReady?: () => Promise<void>
    doc(): unknown
  }>
}

/**
 * Build the `resolveAttachment` seam an InferenceClient wants (RFC 0288 S5).
 *
 * Every host reaches its documents through a repo but constructs one
 * differently, so the seam takes the repo rather than the client importing one.
 * Returns null rather than throwing for a document that will not load: a
 * picture that cannot be fetched must not fail the whole turn, and the message
 * still names the file.
 */
export function makeAttachmentResolver(
  repo: AttachmentReadRepo,
): (docId: string) => Promise<{ mediaType: string; base64: string } | null> {
  // An attachment document is immutable once written, so a hit is the same
  // bytes by construction and there is no invalidation to get wrong.
  //
  // Without it, EVERY turn re-reads and re-base64s every image in the whole
  // transcript: a thread with six screenshots redoes six multi-megabyte
  // encodes on each message, which is pure waste on the latency path.
  //
  // Bounded, because the values are large (base64 is 4/3 of the bytes, so a
  // 5MB cap means ~6.7MB per entry) and a long thread would otherwise pin
  // every image it has ever seen in memory for the life of the process.
  const cache = new Map<string, { mediaType: string; base64: string }>()
  const MAX_ENTRIES = 8

  return async (docId) => {
    const hit = cache.get(docId)
    if (hit) return hit
    try {
      const handle = await repo.find(docId as never)
      await handle.whenReady?.()
      const doc = handle.doc() as AttachmentDoc | undefined
      if (!doc?.content) return null
      const out = {
        mediaType: doc.mimeType,
        base64: toBase64(new Uint8Array(doc.content)),
      }
      // Oldest out first: insertion order is Map's iteration order, and the
      // images being asked for repeatedly are the recent ones.
      if (cache.size >= MAX_ENTRIES) {
        const oldest = cache.keys().next().value
        if (oldest !== undefined) cache.delete(oldest)
      }
      cache.set(docId, out)
      return out
    } catch {
      return null
    }
  }
}
